import { Box, Tooltip, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import ApartmentIcon from '@mui/icons-material/Apartment';
import StorefrontIcon from '@mui/icons-material/Storefront';
import { getUser, getUserRole } from 'src/utils/Common';
import logo from 'src/assets/images/konsep_2.jpg';
import avatar from 'src/assets/images/technician.png';
import { ROLES } from 'src/components/RoleAssignmentPanel';
import { ORANGE } from 'src/components/dynamicCatalogStyles';
import Confetti from 'react-confetti';
import React from 'react';

const AVATAR_SIZE = 120;
const MAX_ROLE_CHIPS = 3;

const useStyles = makeStyles((theme) => ({
  root: {
    fontFamily: 'Segoe UI',
    width: '100%'
  },
  cover: {
    position: 'relative',
    height: 200,
    backgroundSize: 'cover',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center',
    '&::before': {
      position: 'absolute',
      content: '" "',
      top: 0,
      left: 0,
      height: '100%',
      width: '100%',
      backgroundImage: 'linear-gradient(-180deg, rgba(0,0,0,0.00) 58%, rgba(0,0,0,0.32) 100%)'
    }
  },
  content: {
    maxWidth: 1200,
    margin: '0 auto',
    padding: '0 24px 24px',
    boxSizing: 'border-box',
    [theme.breakpoints.down('sm')]: {
      padding: '0 12px 12px'
    }
  },
  // Kapak altına taşan avatar + isim
  header: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: '16px',
    marginTop: -(AVATAR_SIZE / 2),
    paddingLeft: '24px',
    [theme.breakpoints.down('sm')]: {
      paddingLeft: 0
    }
  },
  avatar: {
    width: AVATAR_SIZE,
    height: AVATAR_SIZE,
    borderRadius: '50%',
    border: '4px solid #fff',
    backgroundColor: '#fff3e0',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    objectFit: 'cover',
    flexShrink: 0,
    position: 'relative'
  },
  headerText: {
    paddingBottom: '12px',
    minWidth: 0
  },
  headerLabel: {
    fontSize: '11px',
    fontWeight: 600,
    color: '#78909c',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  headerName: {
    fontSize: '20px',
    fontWeight: 600,
    color: '#263238'
  },
  tabBar: {
    marginTop: '24px',
    borderBottom: '1px solid #e0e0e0',
    display: 'flex'
  },
  tab: {
    padding: '10px 16px',
    fontSize: '12px',
    fontWeight: 600,
    color: '#424242',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    borderBottom: `2px solid ${ORANGE}`,
    marginBottom: '-1px'
  },
  card: {
    marginTop: '24px',
    backgroundColor: '#fff',
    borderRadius: '12px',
    border: '1px solid #eceff1',
    boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
    padding: '24px',
    display: 'flex',
    gap: '20px',
    alignItems: 'center',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'flex-start'
    }
  },
  initials: {
    width: 64,
    height: 64,
    borderRadius: '50%',
    backgroundColor: '#fff3e0',
    color: ORANGE,
    fontSize: '22px',
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0
  },
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    height: '24px',
    padding: '0 10px',
    borderRadius: '12px',
    backgroundColor: '#fff3e0',
    color: '#424242',
    fontSize: '12px',
    fontWeight: 500,
    whiteSpace: 'nowrap',
    '& .MuiSvgIcon-root': { fontSize: '15px', color: ORANGE }
  },
  // Yetki etiketleri bölge/servisten ayrışsın diye nötr tonda
  roleChip: {
    backgroundColor: '#f5f5f5',
    border: '1px solid #e0e0e0',
    color: '#546e7a',
    '& .MuiSvgIcon-root': { color: '#90a4ae' }
  },
  moreChip: {
    backgroundColor: '#eceff1',
    color: '#546e7a',
    fontWeight: 700,
    cursor: 'default'
  },
  messageLine: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '12px',
    color: '#546e7a',
    '& .MuiSvgIcon-root': { fontSize: '17px', color: ORANGE }
  }
}));

const ROLE_LABELS = ROLES.reduce((acc, r) => ({ ...acc, [r.id]: r.label }), {});

function Profile() {
  const classes = useStyles();
  const SessionUser = getUser() || {};
  const userRole = getUserRole() || {};

  const fullName = `${SessionUser.FirstName || ''} ${SessionUser.LastName || ''}`.trim() || 'Kullanıcı';
  const initials = `${(SessionUser.FirstName || '').trim().charAt(0)}${(SessionUser.LastName || '').trim().charAt(0)}`.toLocaleUpperCase('tr-TR');
  const email = (SessionUser.Message || '').split('\n', 3)[1];

  const roleNames = (Array.isArray(userRole.RoleId) ? userRole.RoleId : [])
    .map((r) => ROLE_LABELS[r.ROLE_ID] || r.ROLE_NAME)
    .filter(Boolean);

  const service = [SessionUser.ServiceShopCode, SessionUser.ServiceShopName].filter(Boolean).join(' - ');
  const region = SessionUser.TopServiceShopName;

  const messages = [
    'Dia - Online Arçelik Ürün Değişim Sistemlerine Hoşgeldiniz...',
    email
  ].filter((m) => m && m.trim());

  return (
    <>
      {/* fixed: ekran boyutundaki canvas içerik alanından taşıp kaydırma çubuğu oluşturmasın */}
      {< Confetti style={{ position: 'fixed' }} />}
      <Box className={classes.root}>
        <Box className={classes.cover} style={{ backgroundImage: `url(${logo})` }} />

        <Box className={classes.content}>
          <Box className={classes.header}>
            <img src={avatar} alt="Kullanıcı" className={classes.avatar} />
            <Box className={classes.headerText}>
              <Typography className={classes.headerLabel} noWrap>
                {roleNames.length > 0 ? `Dia · ${roleNames[0]}` : 'Dia'}
              </Typography>
              <Typography className={classes.headerName} noWrap>{fullName}</Typography>
            </Box>
          </Box>

          <Box className={classes.tabBar}>
            <Box className={classes.tab}>Kullanıcı</Box>
          </Box>

          <Box className={classes.card}>
            <Box className={classes.initials}>{initials || '?'}</Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: '15px', fontWeight: 600, color: '#263238' }}>
                Merhaba, {fullName}
              </Typography>

              {/* Bölge / servis */}
              {(region || service) && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                  {region && <span className={classes.chip}><ApartmentIcon />{region}</span>}
                  {service && <span className={classes.chip}><StorefrontIcon />{service}</span>}
                </Box>
              )}

              {/* Yetkiler: ilk MAX_ROLE_CHIPS rol, kalanı +N */}
              {roleNames.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                  {roleNames.slice(0, MAX_ROLE_CHIPS).map((r) => (
                    <span key={r} className={`${classes.chip} ${classes.roleChip}`}><WorkOutlineIcon />{r}</span>
                  ))}
                  {roleNames.length > MAX_ROLE_CHIPS && (
                    <Tooltip
                      arrow
                      title={
                        <Box sx={{ fontSize: '12px', lineHeight: 1.7 }}>
                          {roleNames.slice(MAX_ROLE_CHIPS).map((r) => <div key={r}>{r}</div>)}
                        </Box>
                      }
                    >
                      <span className={`${classes.chip} ${classes.moreChip}`}>+{roleNames.length - MAX_ROLE_CHIPS}</span>
                    </Tooltip>
                  )}
                </Box>
              )}

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
                {messages.map((m, i) => (
                  <Box key={i} className={classes.messageLine}>
                    <CampaignOutlinedIcon />
                    <span>{m}</span>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
}

export default Profile;
